import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams } from './viewerAccess';
import { BORDER_POINT_OPTIONS, labelForValue } from '../component/MarketTrad/marketTradOptions';
import {
  getSharedRecords,
  mergeRecordsByUuid,
  removeSharedRecord,
  upsertSharedRecord,
  upsertSharedRecords,
} from './sharedRecordCache';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';
import { formatGpsCoordinates, toGeoJsonPoint } from '../services/gpsCoordinates';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (
      candidate.uuid ||
      candidate.border_point_id ||
      candidate.crossing_point ||
      candidate.crossingPoint ||
      candidate.crossing_direction ||
      candidate.state ||
      candidate.species_uuid ||
      candidate.origin_state ||
      candidate.originState ||
      candidate.destination_state ||
      candidate.destinationState ||
      candidate.inspection_status ||
      candidate.movement_date
    ) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function textKey(value) {
  return String(value ?? '').trim().toLowerCase();
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

function buildOriginDestination(payload = {}, source = {}) {
  return firstText(
    payload.origin_destination,
    payload.originDestination,
    [payload.origin_country, payload.destination_country].filter(Boolean).join(' / '),
    [payload.originCountry, payload.destinationCountry].filter(Boolean).join(' / '),
    [payload.origin_state, payload.destination_state].filter(Boolean).join(' / '),
    [payload.originState, payload.destinationState].filter(Boolean).join(' / '),
    source.origin_destination,
    source.originDestination,
    [source.origin_country, source.destination_country].filter(Boolean).join(' / '),
    [source.originCountry, source.destinationCountry].filter(Boolean).join(' / '),
    [source.origin_state, source.destination_state].filter(Boolean).join(' / '),
    [source.originState, source.destinationState].filter(Boolean).join(' / ')
  );
}

function matchesBorderCrossingFilters(row = {}, params = {}) {
  const checks = [
    ['state', [row.state]],
    ['species_uuid', [row.species_uuid, row.speciesUuid, row.species_code, row.speciesCode]],
    ['direction', [row.crossing_direction, row.crossingDirection, row.direction]],
    ['data_source', [row.data_source, row.dataSource]],
  ];

  return checks.every(([paramKey, candidates]) => {
    const filterValue = textKey(params[paramKey]);
    if (!filterValue) return true;
    return candidates.some((candidate) => textKey(candidate) === filterValue);
  });
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage = 25 }) {
  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    return extractCollection(data, normalize);
  }

  return {
    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      return extractItem(data, normalize);
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      return extractItem(data, normalize, uuid);
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      return data;
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      return extractItem(data, normalize, uuid);
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      return extractItem(data, normalize, uuid);
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      return extractItem(data, normalize, uuid);
    },

    async reject(uuid, notes = '') {
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes });
      return extractItem(data, normalize, uuid);
    },
  };
}

function normalizeBorderCrossing(row = {}) {
  const speciesObj = row.species && typeof row.species === 'object' ? row.species : {};
  const borderPointObj = row.border_point && typeof row.border_point === 'object' ? row.border_point : {};
  const vetCertObj = row.vet_certificate && typeof row.vet_certificate === 'object' ? row.vet_certificate : {};

  const speciesUuid =
    row.species_uuid ??
    speciesObj.uuid ??
    speciesObj.id ??
    null;

  const borderPointId =
    row.border_point_id ??
    row.borderPointId ??
    row.crossing_point ??
    row.crossingPoint ??
    borderPointObj.uuid ??
    borderPointObj.id ??
    null;

  const speciesCode =
    row.species_code ??
    row.speciesCode ??
    speciesObj.code ??
    speciesObj.species_code ??
    '';

  const speciesLabel =
    row.species_label ??
    row.speciesLabel ??
    speciesObj.common_name_nigeria ??
    speciesObj.commonNameNigeria ??
    speciesObj.name ??
    speciesObj.label ??
    speciesCode ??
    speciesUuid ??
    '';

  const borderPointLabel =
    row.border_point_label ??
    row.borderPointLabel ??
    borderPointObj.name ??
    borderPointObj.label ??
    labelForValue(BORDER_POINT_OPTIONS, borderPointId) ??
    String(borderPointId ?? '');

  const crossingDirection =
    row.crossing_direction ??
    row.crossingDirection ??
    row.direction ??
    '';

  const inspectionStatus =
    row.inspection_status ??
    row.inspectionStatus ??
    row.status ??
    row.validation_status ??
    row.workflow_status ??
    '';

  const dataStatus = row.data_status ?? row.validation_status ?? row.workflow_status ?? 'draft';

  const vetCertificateRef =
    row.vet_certificate_ref ??
    row.vetCertificateRef ??
    row.vet_certificate_uuid ??
    row.vetCertificateUuid ??
    vetCertObj.uuid ??
    vetCertObj.id ??
    null;

  const movementDate = row.movement_date ?? row.movementDate ?? row.crossing_date ?? row.crossingDate ?? '';
  const originDestinationFallback =
    [row.origin_country, row.destination_country].filter(Boolean).join(' / ') ||
    [row.origin_state, row.destination_state].filter(Boolean).join(' / ') ||
    '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    state: row.state ?? '',
    border_point_id: borderPointId,
    borderPointId,
    border_point_label: borderPointLabel,
    borderPointLabel,
    crossing_point: row.crossing_point ?? row.crossingPoint ?? borderPointLabel ?? borderPointId ?? '',
    crossingPoint: row.crossing_point ?? row.crossingPoint ?? borderPointLabel ?? borderPointId ?? '',
    crossing_direction: crossingDirection,
    crossingDirection,
    direction: crossingDirection,
    movement_date: movementDate,
    movementDate,
    crossing_date: row.crossing_date ?? row.crossingDate ?? movementDate,
    crossingDate: row.crossing_date ?? row.crossingDate ?? movementDate,
    origin_country: row.origin_country ?? row.originCountry ?? '',
    originCountry: row.origin_country ?? row.originCountry ?? '',
    destination_country: row.destination_country ?? row.destinationCountry ?? '',
    destinationCountry: row.destination_country ?? row.destinationCountry ?? '',
    origin_state: row.origin_state ?? row.originState ?? '',
    originState: row.origin_state ?? row.originState ?? '',
    destination_state: row.destination_state ?? row.destinationState ?? '',
    destinationState: row.destination_state ?? row.destinationState ?? '',
    origin_destination: row.origin_destination ?? row.originDestination ?? originDestinationFallback,
    originDestination: row.origin_destination ?? row.originDestination ?? originDestinationFallback,
    species_uuid: speciesUuid,
    speciesUuid,
    species_code: speciesCode,
    speciesCode,
    species_label: speciesLabel,
    speciesLabel,
    head_count: toNumberOrNull(row.head_count ?? row.headCount),
    headCount: toNumberOrNull(row.head_count ?? row.headCount),
    vet_certificate_ref: vetCertificateRef,
    vetCertificateRef,
    movement_permit_number: row.movement_permit_number ?? row.movementPermitNumber ?? '',
    movementPermitNumber: row.movement_permit_number ?? row.movementPermitNumber ?? '',
    inspection_status: inspectionStatus,
    inspectionStatus,
    vehicle_id: row.vehicle_id ?? row.vehicleId ?? '',
    vehicleId: row.vehicle_id ?? row.vehicleId ?? '',
    agent_name: row.agent_name ?? row.agentName ?? '',
    agentName: row.agent_name ?? row.agentName ?? '',
    data_source: row.data_source ?? row.dataSource ?? '',
    dataSource: row.data_source ?? row.dataSource ?? '',
    gps_coordinates: formatGpsCoordinates(row.gps_coordinates ?? row.gpsCoordinates ?? null),
    notes: row.notes ?? '',
    status: inspectionStatus,
    status_label: inspectionStatus,
    data_status: dataStatus,
    dataStatus,
    validation_status: row.validation_status ?? '',
    validationStatus: row.validation_status ?? '',
    workflow_status: row.workflow_status ?? '',
    workflowStatus: row.workflow_status ?? '',
    is_active: row.is_active ?? null,
    isActive: row.is_active ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildBorderCrossingPayload(payload = {}) {
  const source = payload.raw ?? payload.existing ?? payload.original ?? {};
  const borderPointId = trimOrNull(
    payload.border_point_id ??
    payload.borderPointId ??
    payload.crossing_point ??
    payload.crossingPoint ??
    source.border_point_id ??
    source.borderPointId ??
    source.crossing_point ??
    source.crossingPoint
  );
  const originDestination = buildOriginDestination(payload, source);
  const borderPointLabel = trimOrNull(
    payload.border_point_label ??
    payload.borderPointLabel ??
    source.border_point_label ??
    source.borderPointLabel ??
    labelForValue(BORDER_POINT_OPTIONS, borderPointId)
  );
  const dataStatus = trimOrNull(
    payload.data_status ??
    payload.dataStatus ??
    source.data_status ??
    source.dataStatus ??
    'draft'
  ) ?? 'draft';

  return {
    state: String(payload.state ?? '').trim(),
    border_point_id: borderPointId,
    borderPointId: borderPointId,
    border_point_label: borderPointLabel,
    borderPointLabel: borderPointLabel,
    crossing_point: borderPointId,
    crossingPoint: borderPointId,
    crossing_direction: trimOrNull(payload.crossing_direction ?? payload.crossingDirection ?? payload.direction ?? payload.direction),
    direction: trimOrNull(payload.crossing_direction ?? payload.crossingDirection ?? payload.direction),
    movement_date: trimOrNull(payload.movement_date ?? payload.movementDate ?? payload.crossing_date ?? payload.crossingDate),
    movementDate: trimOrNull(payload.movement_date ?? payload.movementDate ?? payload.crossing_date ?? payload.crossingDate),
    crossing_date: trimOrNull(payload.movement_date ?? payload.movementDate ?? payload.crossing_date ?? payload.crossingDate),
    crossingDate: trimOrNull(payload.movement_date ?? payload.movementDate ?? payload.crossing_date ?? payload.crossingDate),
    origin_country: trimOrNull(payload.origin_country ?? payload.originCountry),
    destination_country: trimOrNull(payload.destination_country ?? payload.destinationCountry),
    origin_state: trimOrNull(payload.origin_state ?? payload.originState),
    destination_state: trimOrNull(payload.destination_state ?? payload.destinationState),
    species_uuid: trimOrNull(payload.species_uuid ?? payload.speciesUuid),
    head_count: toNumberOrNull(payload.head_count ?? payload.headCount),
    origin_destination: originDestination || null,
    originDestination: originDestination || null,
    vet_certificate_ref: trimOrNull(payload.vet_certificate_ref ?? payload.vetCertificateRef ?? source.vet_certificate_ref ?? source.vetCertificateRef) ?? null,
    vetCertificateRef: trimOrNull(payload.vet_certificate_ref ?? payload.vetCertificateRef ?? source.vet_certificate_ref ?? source.vetCertificateRef) ?? null,
    movement_permit_number: trimOrNull(payload.movement_permit_number ?? payload.movementPermitNumber ?? source.movement_permit_number ?? source.movementPermitNumber),
    movementPermitNumber: trimOrNull(payload.movement_permit_number ?? payload.movementPermitNumber ?? source.movement_permit_number ?? source.movementPermitNumber),
    inspection_status: trimOrNull(payload.inspection_status ?? payload.inspectionStatus ?? payload.status ?? source.inspection_status ?? source.inspectionStatus ?? source.status),
    status: trimOrNull(payload.inspection_status ?? payload.inspectionStatus ?? payload.status ?? source.inspection_status ?? source.inspectionStatus ?? source.status),
    data_status: dataStatus,
    vehicle_id: trimOrNull(payload.vehicle_id ?? payload.vehicleId),
    agent_name: trimOrNull(payload.agent_name ?? payload.agentName),
    data_source: trimOrNull(payload.data_source ?? payload.dataSource),
    notes: trimOrNull(payload.notes),
    gps_coordinates: toGeoJsonPoint(payload.gps_coordinates ?? payload.gpsCoordinates ?? source.gps_coordinates ?? source.gpsCoordinates),
  };
}

const borderCrossingsBaseApi = makeCollectionApi({
  basePath: '/v1/border-crossings',
  normalize: normalizeBorderCrossing,
  buildPayload: buildBorderCrossingPayload,
  defaultPerPage: 25,
});

const BORDER_CROSSINGS_CACHE_KEY = 'border-crossings';

function cacheBorderCrossings(rows = []) {
  upsertSharedRecords(BORDER_CROSSINGS_CACHE_KEY, rows);
}

function filterBorderCrossingsByParams(rows = [], params = {}) {
  return rows.filter((row) => matchesBorderCrossingFilters(row, params));
}

const borderCrossingsLegacyApi = {
  ...borderCrossingsBaseApi,

  async listAll(params = {}) {
    const live = await borderCrossingsBaseApi.listAll(params);
    cacheBorderCrossings(live.items ?? []);
    const cached = filterBorderCrossingsByParams(getSharedRecords(BORDER_CROSSINGS_CACHE_KEY), params);
    return {
      ...live,
      items: mergeRecordsByUuid(live.items ?? [], cached),
    };
  },

  async get(uuid) {
    try {
      const record = await borderCrossingsBaseApi.get(uuid);
      if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
      return record;
    } catch (error) {
      const cached = getSharedRecords(BORDER_CROSSINGS_CACHE_KEY).find(
        (row) => String(row?.uuid ?? row?.id ?? '').trim() === String(uuid ?? '').trim()
      );
      if (cached) return normalizeBorderCrossing(cached);
      throw error;
    }
  },

  async create(payload) {
    const record = await borderCrossingsBaseApi.create(payload);
    if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
    return record;
  },

  async update(uuid, payload) {
    const record = await borderCrossingsBaseApi.update(uuid, payload);
    if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
    return record;
  },

  async remove(uuid) {
    const response = await borderCrossingsBaseApi.remove(uuid);
    removeSharedRecord(BORDER_CROSSINGS_CACHE_KEY, uuid);
    return response;
  },

  async submit(uuid) {
    const record = await borderCrossingsBaseApi.submit(uuid);
    if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
    return record;
  },

  async validate(uuid) {
    const record = await borderCrossingsBaseApi.validate(uuid);
    if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
    return record;
  },

  async approve(uuid) {
    const record = await borderCrossingsBaseApi.approve(uuid);
    if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
    return record;
  },

  async reject(uuid, notes = '') {
    if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
    const record = await borderCrossingsBaseApi.reject(uuid, notes.trim());
    if (record?.uuid) upsertSharedRecord(BORDER_CROSSINGS_CACHE_KEY, record);
    return record;
  },
};

export const borderCrossingsApi = withOfflineCollectionSupport(borderCrossingsLegacyApi, {
  basePath: '/v1/border-crossings',
  collectionKey: 'border-crossings',
  normalize: normalizeBorderCrossing,
  buildPayload: buildBorderCrossingPayload,
});

export { normalizeBorderCrossing, buildBorderCrossingPayload };
